import { LoadingOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Modal, theme } from 'antd';
import { type Dayjs } from 'dayjs';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomerOrderFields from './customer-order-fields';
import {
  getInitialOrderDate,
  getInitialSiteMenuOrders,
  getOrderSites,
  type EditableSiteMenuOrder,
  type SiteMenuOrdersBySite,
  sortSiteMenuOrders,
  withSiteMenuOrders,
} from './customer-order-form.utils';
import CustomerOrderSummaryModal from './customer-order-summary.modal';
import CustomerOrderTotals from './customer-order-totals';
import { useSitesHavingOrdersOnDate } from './hooks/use-sites-having-orders-on-date';
import SitesCollapse from './sites-collapse';
import useKeepNewOrderInCart from './use-keep-new-order-in-cart';
import useSubmitCustomerOrder from './use-submit-customer-order';
import { useCartContext } from '../../../../contexts/cart.context';
import { type Customer, type CustomerOrder } from '../../../../entities';
import { useNotification } from '../../../../hooks/use-notification';
import { LocalDate } from '../../../../utils/local-date';
import SitesWithExistingOrdersModal from '../sites-with-existing-orders.modal';

interface CustomerOrderFormProps {
  /** The order to update, or the new order to place when it has no id. Its customer is selected */
  customerOrder: CustomerOrder;
  /** The customers an order can be placed for, with their sites and settings */
  customers: Customer[];
}

const CustomerOrderForm = ({ customerOrder, customers }: CustomerOrderFormProps): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const { clearCart } = useCartContext();
  const [searchParams] = useSearchParams();
  const isUpdating = !!customerOrder.id;

  // The order only carries its customer's id: the customer's sites and settings come from "customers"
  const [customer, setCustomer] = useState(() =>
    customers.find((cust) => cust.id === customerOrder.customer?.id)
  );
  const sites = useMemo(() => getOrderSites(customer, customerOrder), [customer, customerOrder]);

  const [orderDate, setOrderDate] = useState<LocalDate | undefined>(() =>
    customer
      ? getInitialOrderDate(customerOrder, customer, searchParams.get('preSelectedOrderDate'))
      : customerOrder.orderDate || undefined
  );

  // Get Sites Already Have Orders On The Selected Date
  const { sitesAlreadyHaveOrders } = useSitesHavingOrdersOnDate(customerOrder, sites, orderDate);

  const [siteMenuOrdersBySite, setSiteMenuOrdersBySite] = useState<SiteMenuOrdersBySite>(() =>
    getInitialSiteMenuOrders(sites, customerOrder)
  );
  // The sites whose site menu order is being added or edited, which holds the submission back
  // React re-renders the component the object is changed not modified so we use ReadonlySet to avoid accidental modification of the set.
  // The set is replaced with a new one when a site is added or removed from it.
  const [editingSiteIds, setEditingSiteIds] = useState<ReadonlySet<string>>(new Set());
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  const siteMenuOrders = useMemo(
    () => sites.flatMap((site) => siteMenuOrdersBySite[site.id!] ?? []),
    [sites, siteMenuOrdersBySite]
  );
  const sitesWithSiteMenuOrders = useMemo(
    () => withSiteMenuOrders(sites, siteMenuOrdersBySite),
    [sites, siteMenuOrdersBySite]
  );
  const markSubmitted = useKeepNewOrderInCart(!isUpdating, {
    customerId: customer?.id,
    orderDate,
    siteMenuOrders,
  });
  const closeSummary = useCallback(() => {
    setIsSummaryOpen(false);
  }, []);
  const submission = useSubmitCustomerOrder(customerOrder, {
    onSettled: closeSummary,
    onSaved: markSubmitted,
  });

  const handleCustomerChange = (customerId: string): void => {
    const selectedCustomer = customers.find((cust) => cust.id === customerId);
    if (!selectedCustomer) return;
    setCustomer(selectedCustomer);
    setOrderDate(
      getInitialOrderDate(customerOrder, selectedCustomer, searchParams.get('preSelectedOrderDate'))
    );
    setSiteMenuOrdersBySite(
      getInitialSiteMenuOrders(getOrderSites(selectedCustomer, customerOrder), customerOrder)
    );
    setEditingSiteIds(new Set());
  };

  // The meals ordered for another date don't apply to the new one
  const handleOrderDateChange = (date: Dayjs | null): void => {
    setOrderDate(date ? new LocalDate(date) : undefined);
    setSiteMenuOrdersBySite({});
    setEditingSiteIds(new Set());
  };

  const handleSiteMenuOrdersChange = useCallback(
    (siteId: string, siteMenuOrders: EditableSiteMenuOrder[]) => {
      setSiteMenuOrdersBySite((previous) => ({
        ...previous,
        [siteId]: sortSiteMenuOrders(siteMenuOrders),
      }));
    },
    []
  );

  const handleEditingChange = useCallback((siteId: string, isEditing: boolean) => {
    setEditingSiteIds((previous) => {
      const next = new Set(previous);
      if (isEditing) next.add(siteId);
      else next.delete(siteId);
      return next;
    });
  }, []);

  const handleDiscard = (): void => {
    Modal.confirm({
      title: 'Discard changes?',
      content: 'All unsaved changes will be lost.',
      okText: 'Discard',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: () => {
        if (isUpdating) {
          setSiteMenuOrdersBySite(getInitialSiteMenuOrders(sites, customerOrder));
        } else {
          setSiteMenuOrdersBySite({});
          clearCart();
        }
        notification.success({
          description:
            'Changes have been discarded and the order has been reverted to its original state.',
          message: 'Changes discarded',
        });
      },
    });
  };

  const handleSubmit = (): void => {
    if (!customer?.id || !orderDate) return;
    submission.submit({ customerId: customer.id, orderDate, sites: sitesWithSiteMenuOrders });
  };

  const submitLabel = submission.isSubmitting
    ? isUpdating
      ? 'Updating orders ...'
      : 'Submitting orders ...'
    : isUpdating
    ? 'Update Order'
    : 'Submit orders';

  return (
    <Flex vertical gap={16}>
      <CustomerOrderFields
        customers={customers}
        customer={customer}
        orderDate={orderDate}
        isUpdating={isUpdating}
        onCustomerChange={handleCustomerChange}
        onOrderDateChange={handleOrderDateChange}
      />
      <Card styles={{ body: { padding: 0 } }}>
        <SitesCollapse
          sites={sites}
          sitesAlreadyHaveOrders={sitesAlreadyHaveOrders}
          siteMenuOrdersBySite={siteMenuOrdersBySite}
          editingSiteIds={editingSiteIds}
          orderDate={orderDate}
          customerSetting={customer?.customerSetting}
          defaultActiveSiteId={searchParams.get('preSelectedSiteId')}
          onSiteMenuOrdersChange={handleSiteMenuOrdersChange}
          onEditingChange={handleEditingChange}
        />
      </Card>
      <CustomerOrderTotals siteMenuOrders={siteMenuOrders} />
      <Flex
        justify="center"
        gap={token.marginLG}
        flex="0 0 100%"
        style={{ marginBlock: token.marginSM }}
      >
        <Button
          block
          size="large"
          type="primary"
          style={{ maxWidth: 500 }}
          onClick={() => {
            setIsSummaryOpen(true);
          }}
          icon={submission.isSubmitting ? <LoadingOutlined /> : undefined}
          disabled={submission.isSubmitting || editingSiteIds.size > 0}
        >
          {submitLabel}
        </Button>
        <Button block size="large" type="default" style={{ maxWidth: 500 }} onClick={handleDiscard}>
          Discard
        </Button>
      </Flex>
      <CustomerOrderSummaryModal
        open={isSummaryOpen}
        sites={sitesWithSiteMenuOrders}
        isUpdating={isUpdating}
        confirmLoading={submission.isSubmitting}
        onOk={handleSubmit}
        onCancel={closeSummary}
      />
      <SitesWithExistingOrdersModal
        isOpen={submission.isExistingOrdersWarningOpen}
        sites={submission.sitesWithExistingOrders}
        orderDate={orderDate}
        onOk={submission.confirmExistingOrders}
        onCancel={submission.cancelExistingOrders}
      />
    </Flex>
  );
};

export default CustomerOrderForm;
